const {
    INCIDENT_TYPE,
    RISK_LEVELS,
    SECURITY_EVENTS_TYPES,
    ALERTS_MANAGEMENT_OPTIONS_TYPES,
    AUTHENTICATION_EVENTS_TYPES,
    SYSLOG_MSG_TYPES,
    NEW_PRICING_MODULE_FIELDS_TITLES_OBJECT,
    CLOUD_EVENTS_TYPES
} = require('@silverfort-nodelibs/common/utils/consts');
const { RISK_INDICATORS } = require('@silverfort-nodelibs/common/utils/insight');
const { TOOLTIP_PROP_OPTIONS } = require('../../../../common/LockedFeatures');

export const RISKS = Object.values(RISK_INDICATORS)
    .filter((ri) => ri.isPostureInsight || ri.isPostureInsightForNotification)
    .map((ri) => ({ label: ri.insightsTitle, value: ri.riskRules }));

export const CUSTOMIZABLE_EVENTS = new Set([
    SECURITY_EVENTS_TYPES.NEW_INCIDENT.id,
    SECURITY_EVENTS_TYPES.INCIDENT_AUTH_LOGS.id,
    SECURITY_EVENTS_TYPES.NEW_INSIGHT.id,
    SECURITY_EVENTS_TYPES.RISK_LEVEL.id,
    SECURITY_EVENTS_TYPES.POLICY_MATCH.id
]);

export const EVENT_MODULES_MAP = {
    [CLOUD_EVENTS_TYPES.CLOUD_NEW_THREAT.id]: {
        requiredModules: [NEW_PRICING_MODULE_FIELDS_TITLES_OBJECT.ITDR],
        tooltip: { ...TOOLTIP_PROP_OPTIONS.ITDR_MODULE_REQUIRED }
    },
    [CLOUD_EVENTS_TYPES.IDENTITY_RISK_CHANGED.id]: {
        requiredModules: [NEW_PRICING_MODULE_FIELDS_TITLES_OBJECT.ITDR],
        tooltip: {
            ...TOOLTIP_PROP_OPTIONS.ITDR_MODULE_REQUIRED,
            onGetAccessClick: () =>
                window.open('https://www.silverfort.com/itdr-ispm-signup/?utm_source=PQL&utm_content=locked', '_blank')
        }
    },
    [CLOUD_EVENTS_TYPES.CLOUD_NEW_EXPOSURE.id]: {
        requiredModules: [NEW_PRICING_MODULE_FIELDS_TITLES_OBJECT.ISPM],
        tooltip: { ...TOOLTIP_PROP_OPTIONS.ISPM_MODULE_REQUIRED }
    },
    [CLOUD_EVENTS_TYPES.CLOUD_EXPOSURE_CHANGED.id]: {
        requiredModules: [NEW_PRICING_MODULE_FIELDS_TITLES_OBJECT.ISPM],
        tooltip: { ...TOOLTIP_PROP_OPTIONS.ISPM_MODULE_REQUIRED }
    },
    [SECURITY_EVENTS_TYPES.NEW_INCIDENT.id]: {
        requiredModules: [NEW_PRICING_MODULE_FIELDS_TITLES_OBJECT.ITDR],
        tooltip: { ...TOOLTIP_PROP_OPTIONS.ITDR_MODULE_REQUIRED }
    },
    [SECURITY_EVENTS_TYPES.RISK_LEVEL.id]: {
        requiredModules: [NEW_PRICING_MODULE_FIELDS_TITLES_OBJECT.ITDR],
        tooltip: {
            ...TOOLTIP_PROP_OPTIONS.ITDR_MODULE_REQUIRED,
            onGetAccessClick: () =>
                window.open('https://www.silverfort.com/itdr-ispm-signup/?utm_source=PQL&utm_content=locked', '_blank')
        }
    },
    [SECURITY_EVENTS_TYPES.NEW_INSIGHT.id]: {
        requiredModules: [NEW_PRICING_MODULE_FIELDS_TITLES_OBJECT.ISPM],
        tooltip: {
            ...TOOLTIP_PROP_OPTIONS.ISPM_MODULE_REQUIRED
        }
    },
    [SECURITY_EVENTS_TYPES.POLICY_MATCH.id]: {
        requiredModules: [
            NEW_PRICING_MODULE_FIELDS_TITLES_OBJECT.MFA_USERS,
            NEW_PRICING_MODULE_FIELDS_TITLES_OBJECT.AUTHENTICATION_FIREWALL
        ],
        tooltip: {
            ...TOOLTIP_PROP_OPTIONS.MFA_AND_AUTHENTICATION_FIREWALL_REQUIRED
        }
    },
    [SECURITY_EVENTS_TYPES.SA_ALERTS.id]: {
        requiredModules: [NEW_PRICING_MODULE_FIELDS_TITLES_OBJECT.NHI_PROTECTION],
        tooltip: {
            ...TOOLTIP_PROP_OPTIONS.NHI_PROTECTION_LICENSE_REQUIRED,
            onGetAccessClick: () =>
                window.open('https://www.silverfort.com/nhi-signup/?utm_source=PQL&utm_content=locked', '_blank')
        }
    },
    [SECURITY_EVENTS_TYPES.PA_ALERTS.id]: {
        requiredModules: [NEW_PRICING_MODULE_FIELDS_TITLES_OBJECT.PRIVILEGED_ACCESS_SECURITY],
        tooltip: {
            ...TOOLTIP_PROP_OPTIONS.NHI_PROTECTION_LICENSE_REQUIRED
        }
    }
};

export const NOTIFICATION_EVENTS_SETTINGS = {
    [SECURITY_EVENTS_TYPES.NEW_INCIDENT.id]: {
        title: 'Choose Incidents',
        desc: `Select the incident types that trigger this {alertType}. Incidents are a potential identity threat attack involving several authentications and entities within a specific time period.`,
        options: Object.values(INCIDENT_TYPE).map((type) => ({ label: type.guiName, value: type.value })),
        defaultSelections: Object.values(INCIDENT_TYPE).map((type) => type.value)
    },
    [SECURITY_EVENTS_TYPES.INCIDENT_AUTH_LOGS.id]: {
        title: 'Choose Incidents',
        desc: `Select the incident types that trigger this {alertType}. Incidents are a potential identity threat attack involving several authentications and entities within a specific time period.`,
        options: Object.values(INCIDENT_TYPE).map((type) => ({ label: type.guiName, value: type.value })),
        defaultSelections: Object.values(INCIDENT_TYPE).map((type) => type.value)
    },
    [SECURITY_EVENTS_TYPES.RISK_LEVEL.id]: {
        title: 'Choose Risk Level',
        desc: `Choose the risk level that triggers this {alertType}.`,
        options: [
            { value: RISK_LEVELS.CRITICAL.value, label: 'Critical' },
            { value: RISK_LEVELS.HIGH.value, label: 'High or above' }
        ],
        optionType: ALERTS_MANAGEMENT_OPTIONS_TYPES.RADIO,
        defaultSelections: [RISK_LEVELS.CRITICAL.value]
    },
    [SECURITY_EVENTS_TYPES.POLICY_MATCH.id]: {
        title: 'Choose Policies',
        desc: `Choose to send this {alertType} when certain policies are triggered. For MFA policies, the {alertType} is triggered only for authentications where the MFA result is Deny or time out.`
    },
    [SECURITY_EVENTS_TYPES.NEW_INSIGHT.id]: {
        title: 'New Posture Exposure',
        desc: 'Get notified when a new posture exposure is added to an entity.',
        options: RISKS,
        defaultSelections: RISKS.map((option) => option.value)
    },
    [AUTHENTICATION_EVENTS_TYPES.AUTHENTICATIONS.id]: {
        title: 'Authentications',
        multiOptions: true,
        options: {
            Protocols: {
                options: [
                    SYSLOG_MSG_TYPES.NTLM_AUTH,
                    SYSLOG_MSG_TYPES.KERB_AUTH,
                    SYSLOG_MSG_TYPES.LDAP_AUTH,
                    SYSLOG_MSG_TYPES.AAD_SSO_AUTH,
                    SYSLOG_MSG_TYPES.OCTA_SSO_AUTH,
                    SYSLOG_MSG_TYPES.RADIUS_AUTH,
                    SYSLOG_MSG_TYPES.SAML_AUTH,
                    SYSLOG_MSG_TYPES.OIDC_AUTH,
                    SYSLOG_MSG_TYPES.DESKTOP_AUTH,
                    SYSLOG_MSG_TYPES.SF_API_AUTH
                ]
            },
            'Additional Filters': {
                optionType: ALERTS_MANAGEMENT_OPTIONS_TYPES.RADIO,
                options: [
                    { label: 'None', value: SYSLOG_MSG_TYPES.AUTH.value },
                    { label: 'MFA only', value: SYSLOG_MSG_TYPES.MFA.value },
                    { label: 'Service Accounts only', value: SYSLOG_MSG_TYPES.SERVICE_ACCOUNTS.value }
                ]
            }
        },
        defaultSelections: [
            SYSLOG_MSG_TYPES.NTLM_AUTH.value,
            SYSLOG_MSG_TYPES.KERB_AUTH.value,
            SYSLOG_MSG_TYPES.LDAP_AUTH.value,
            SYSLOG_MSG_TYPES.AAD_SSO_AUTH.value,
            SYSLOG_MSG_TYPES.OCTA_SSO_AUTH.value,
            SYSLOG_MSG_TYPES.RADIUS_AUTH.value,
            SYSLOG_MSG_TYPES.SAML_AUTH.value,
            SYSLOG_MSG_TYPES.OIDC_AUTH.value,
            SYSLOG_MSG_TYPES.DESKTOP_AUTH.value,
            SYSLOG_MSG_TYPES.SF_API_AUTH.value,
            SYSLOG_MSG_TYPES.AUTH.value
        ]
    }
};
